import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import {
  IAvailableEndpoint, IMcpTool, IMcpToolUpdate
} from "../../interfaces/endpoints/IMcpTool";
import {
  updateMcpTool, createMcpTool, getAvailableEndpoints
} from "../../services/Mcp.service";
import McpToolModalContent from "./McpToolModalContent";

interface McpToolModalProps {
  mode: "edit" | "create";
  tool?: IMcpTool;
  onClose: () => void;
  onSaved: () => void;
}

const EMPTY: IMcpToolUpdate = {
  toolName: "",
  description: "",
  httpMethod: "GET",
  endpoint: "",
  inputSchemaJson: "{\"type\":\"object\",\"properties\":{}}",
  requiredPermission: "",
  category: "",
  isEnabled: true
};

const McpToolModal: React.FC<McpToolModalProps> = ({
  mode, tool, onClose, onSaved
}) => {
  const init = tool
    ? { ...tool, isEnabled: tool.isEnabled } as IMcpToolUpdate
    : { ...EMPTY };
  const [form, setForm] = useState<IMcpToolUpdate>(init);
  const [saving, setSaving] = useState(false);
  const [endpoints, setEndpoints] = useState<IAvailableEndpoint[]>([]);
  const isEdit = mode === "edit";

  useEffect(() => {
    getAvailableEndpoints()
      .then((res) => setEndpoints(
        Array.isArray(res.data) ? res.data : []
      ))
      .catch(() => undefined);
  }, []);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setForm((p) => ({
      ...p,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleEndpointSelect = (ep: IAvailableEndpoint) => {
    setForm((p) => ({
      ...p,
      endpoint: ep.endpoint,
      httpMethod: ep.method,
      inputSchemaJson: ep.inputSchema || p.inputSchemaJson,
      category: ep.groupName || p.category
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit && tool) {
        await updateMcpTool(tool.id, form);
        toast.success("Tool updated");
      } else {
        await createMcpTool(form);
        toast.success("Tool created");
      }
      onSaved();
    } catch {
      toast.error(isEdit
        ? "Failed to update tool"
        : "Failed to create tool");
    } finally {
      setSaving(false);
    }
  };

  const handleBackdrop = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 bg-black bg-opacity-50
        z-50 flex items-center justify-center"
      onClick={handleBackdrop}
    >
      <McpToolModalContent
        form={form} setForm={setForm}
        isEdit={isEdit} saving={saving}
        endpoints={endpoints}
        onChange={handleChange}
        onEndpointSelect={handleEndpointSelect}
        onSubmit={handleSubmit}
        onClose={onClose}
      />
    </div>,
    document.body
  );
};

export default McpToolModal;
